import { useInfiniteQuery } from '@tanstack/react-query';

import { getWorkspaceFileHistory } from '@/lib/api/generated/api';
import type { FileHistoryPage } from '@/lib/api/generated/model';
import { fileKeys } from '../queryKeys';

/** Fixed page size; how far back the reader has asked to go is held in the cache. */
export const HISTORY_PAGE_SIZE = 50;

/**
 * The change log holds no live event for this path — which is not the same as
 * the request failing, and must not be shown as one.
 *
 * History is derived from the log alone, never from a storage check
 * (research.md D9), so the service answers 404 for a path it has recorded
 * nothing about. A file that was uploaded before the log existed is exactly
 * that case: it is sitting in the listing, and it genuinely has no history.
 */
const NO_HISTORY = 'no-history' as const;

type HistoryResult = FileHistoryPage | typeof NO_HISTORY | null;

/** Narrows the sentinel out; inference alone does not reach through `query.data`. */
function isPage(result: HistoryResult | undefined): result is FileHistoryPage {
  return result !== null && result !== undefined && result !== NO_HISTORY;
}

/**
 * A file's change history, newest-first (server-sorted, per FR-006/FR-F006 —
 * this hook never re-sorts), read a page at a time and accumulated.
 *
 * Infinite rather than offset-per-render: reading further back used to replace
 * the list with the next page, so following a file's story meant losing your
 * place in the part you had already read. Older entries land on the end now,
 * and how far back the reader has gone lives in the cache rather than in the
 * address — what is worth sharing is the file's history, not page three of it.
 *
 * Both the history view and the preview header's "last changed by" line read
 * this same query, so heading a file with its provenance costs no request.
 *
 * Same `422`-resolves-to-`null` asymmetry as `useWorkspaceFileListing`: a
 * validation failure (malformed `path`) is not the same as a request that
 * never came back.
 */
export function useWorkspaceFileHistory(workspaceId: string, path: string) {
  const query = useInfiniteQuery({
    queryKey: fileKeys.history(workspaceId, path),
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<HistoryResult> => {
      const response = await getWorkspaceFileHistory(workspaceId, {
        path,
        limit: HISTORY_PAGE_SIZE,
        offset: pageParam,
      });
      if (response.status === 200) return response.data;
      if (response.status === 404) return NO_HISTORY;
      return null;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!isPage(lastPage)) return undefined;
      const loaded = allPages.reduce(
        (count, page) => count + (isPage(page) ? page.items.length : 0),
        0,
      );
      // A page that comes back empty while the total says otherwise would
      // otherwise ask for the same offset forever.
      return loaded < lastPage.total && lastPage.items.length > 0
        ? loaded
        : undefined;
    },
    retry: false,
  });

  const pages = query.data?.pages ?? [];
  const firstPage = pages[0];

  return {
    items: pages.flatMap((page) => (isPage(page) ? page.items : [])),
    /** Everything the log holds for this file, not just what has been read. */
    total: isPage(firstPage) ? firstPage.total : 0,
    /** True when the log simply has nothing for this path. */
    noHistory: firstPage === NO_HISTORY,
    isPending: query.isPending,
    loadFailed: !query.isPending && (query.isError || firstPage === null),
    hasOlder: query.hasNextPage,
    isLoadingOlder: query.isFetchingNextPage,
    loadOlder: query.fetchNextPage,
    refetch: query.refetch,
  };
}
